import {
  CategoryDetail,
  CategoryType,
  CompareOption,
  Facet,
  FilterOption,
  Identifiers,
  SearchCategoryType,
  SearchCompanyInfo,
  SearchDocument,
  SearchFilter,
  SearchRequest,
  SearchSortField,
  SearchSortNestedField,
  SearchState,
  SearchTarget,
  SortDirection,
  SUPPLIER_SEARCH_TARGET
} from 'types/Search';
import { convertArrayToCommaString } from './utils';
import { SegmentTag } from '../types/global';
import { ProductType } from 'types/Products';
import { IngredientsList } from 'types/BrandPortal/Ingredients';
import {
  AssetTypes,
  CompanyTypes,
  Regions,
  ServiceTypes,
  translate
} from 'api/types/ReferenceData';
import { Countries } from 'api/types/countries';
import {
  CustomAttributesTypes,
  FilterFacetsTypes,
  UnionCustomAttributeProps
} from 'components/common/Portals/CustomAttributes/types';
import { IS_UXBEAUTY } from 'components/constants/Constants';
import { generateRandomSortSeed } from 'api/services/SearchService';
import { getUxbFilterOptions } from 'components/common/Search/SearchHeader/uxbeautyFilterOptions';
import { t } from 'i18next';

const itemInfoDisplayPriorityList = [
  'inci',
  'claims',
  'sustainabilityClaims',
  'functions',
  'applications',
  'certifications',
  'chemicalgroup',
  'cas',
  'iupac',
  'appearances',
  'properties',
  'organoleptic'
];

const foodInfoDisplayPriorityList = [
  'functions',
  'applications',
  'claims',
  'sustainabilityClaims',
  'food-categoties',
  'shel-life',
  'ec',
  'e',
  'fema',
  'flavis',
  'jecfa',
  'inci',
  'certifications',
  'chemicalgroup',
  'cas',
  'iupac',
  'appearances',
  'properties',
  'organoleptic'
];

const filterOptionsListFoodIngredients: FilterOption[] = [
  {
    key: 'document_types',
    label: 'Documents'
  },
  {
    key: 'companies',
    label: 'Companies'
  },
  {
    key: 'food_categories',
    label: 'Food categories'
  },
  {
    key: 'functions',
    label: 'Functions'
  },
  {
    key: 'active_components',
    label: 'Active components'
  },
  {
    key: 'category',
    label: 'Category'
  },
  {
    key: 'sustainability_claims',
    label: 'Sustainability Claims'
  },
  {
    key: 'performance_claims',
    label: 'Performance Claims'
  },
  {
    key: 'applications',
    label: 'Applications'
  },
  {
    key: 'chemicalgroups',
    label: 'Chemical Groups'
  },
  {
    key: 'physical_properties',
    label: 'Physical Properties'
  },
  {
    key: 'chemical_properties',
    label: 'Chemical Properties'
  },
  {
    key: 'color',
    label: 'Color'
  },
  {
    key: 'appearances',
    label: 'Appearances'
  },
  {
    key: 'flavor_family',
    label: 'Flavor family'
  },
  {
    key: 'flavor',
    label: 'Flavor'
  },
  {
    key: 'free_from_claims',
    label: 'Free from claims'
  },
  {
    key: 'certifications',
    label: 'Certifications'
  },
  {
    key: 'regions',
    label: 'Regions available'
  },
  {
    key: 'countries',
    label: 'Countries available'
  }
];

export const filterOptionsListIngredients: FilterOption[] = [
  {
    key: 'document_types',
    label: 'Documents'
  },
  {
    key: 'companies',
    label: 'Companies'
  },
  {
    key: 'functions',
    label: 'Functions'
  },
  {
    key: 'category',
    label: 'Category'
  },
  {
    key: 'sustainability_claims',
    label: 'Sustainability Claims'
  },
  {
    key: 'performance_claims',
    label: 'Performance Claims'
  },
  {
    key: 'applications',
    label: 'Applications'
  },
  { key: 'origin', label: 'Origin' },
  {
    key: 'chemicalgroups',
    label: 'Chemical Groups'
  },
  {
    key: 'physical_properties',
    label: 'Physical Properties'
  },
  {
    key: 'chemical_properties',
    label: 'Chemical Properties'
  },
  {
    key: 'color',
    label: 'Color'
  },
  {
    key: 'appearances',
    label: 'Appearances'
  },
  {
    key: 'certifications',
    label: 'Certifications'
  },
  {
    key: 'regions',
    label: 'Regions available'
  },
  {
    key: 'countries',
    label: 'Countries available'
  }
];

const filterOptionsListFormulations: FilterOption[] = [
  {
    key: 'companies',
    label: 'Companies'
  },
  {
    key: 'applications',
    label: 'Applications'
  },
  {
    key: 'sustainability_claims',
    label: 'Sustainability Claims'
  },
  {
    key: 'performance_claims',
    label: 'Performance Claims'
  }
];

const filterOptionsListServices: FilterOption[] = [
  {
    key: 'companies',
    label: 'Companies'
  },
  {
    key: 'services',
    label: 'Services'
  },
  {
    key: 'applications',
    label: 'Applications'
  },
  {
    key: 'regions',
    label: 'Regions available'
  },
  {
    key: 'countries',
    label: 'Countries available'
  }
];

export const compareAttributesListIngredients: CompareOption[] = [
  {
    key: 'inci',
    label: 'INCI name'
  },
  {
    key: 'iupac',
    label: 'IUPAC name'
  },
  {
    key: 'cas',
    label: 'CAS numbers'
  },
  {
    key: 'performance_claims',
    label: 'Performance claims	'
  },
  {
    key: 'sustainability_claims',
    label: 'Sustainability claims	'
  },
  {
    key: 'functions',
    label: 'Function'
  },
  {
    key: 'applications',
    label: 'Applications'
  },
  {
    key: 'usagelevel',
    label: 'Usage level'
  },
  {
    key: 'chemicalgroup',
    label: 'Chemical group'
  },
  {
    key: 'chemical_properties',
    label: 'Chemical properties'
  },
  {
    key: 'physical_properties',
    label: 'Physical properties'
  },
  {
    key: 'appearance_organoleptic',
    label: 'Appearance'
  },
  {
    key: 'colors_organoleptic',
    label: 'Colors'
  },
  {
    key: 'origin',
    label: 'Origin'
  },
  {
    key: 'origin-species',
    label: 'Origin Species'
  },
  {
    key: 'regional-availability',
    label: 'Regions available'
  },
  {
    key: 'countriesAvailable',
    label: 'Countries available'
  },
  {
    key: 'hazardclassification',
    label: 'GHS classification'
  },
  {
    key: 'hazardstatements',
    label: 'Hazard statements'
  },
  {
    key: 'certifications',
    label: 'Certifications'
  },
  {
    key: 'rci',
    label: 'Renewable Carbon Index (RCI)'
  }
];
export const compareAttributesListFood = [
  //TODO: filter the things not needed in food
  //and sort

  //Food identifiers
  { key: 'general_name', label: 'General name' },
  { key: 'ec', label: t('products.identifiers.ec') },
  { key: 'e', label: t('products.identifiers.e') },
  { key: 'fema', label: t('products.identifiers.fema') },
  {
    key: 'flavis',
    label: t('products.identifiers.flavis')
  },
  {
    key: 'jecfa',
    label: t('products.identifiers.jecfa')
  },
  { key: 'coe', label: t('products.identifiers.coe') },
  {
    key: 'iupac',
    label: 'IUPAC name'
  },
  {
    key: 'cas',
    label: 'CAS numbers'
  },
  {
    key: 'performance_claims',
    label: 'Performance claims	'
  },
  {
    key: 'sustainability_claims',
    label: 'Sustainability claims	'
  },
  {
    key: 'free_from_claims',
    label: 'Free from claims'
  },
  {
    key: 'functions',
    label: 'Function'
  },
  {
    key: 'applications',
    label: 'Applications'
  },
  {
    key: 'food-categories',
    label: 'Food categories'
  },
  {
    key: 'usagelevel',
    label: 'Usage level'
  },
  {
    key: 'active-components',
    label: 'Active components'
  },
  {
    key: 'chemicalgroup',
    label: 'Chemical group'
  },
  {
    key: 'chemical_properties',
    label: 'Chemical properties'
  },
  {
    key: 'physical_properties',
    label: 'Physical properties'
  },
  {
    key: 'appearance_organoleptic',
    label: 'Appearance'
  },
  {
    key: 'colors_organoleptic',
    label: 'Colors'
  },
  {
    key: 'flavor_family_organoleptic',
    label: 'Flavor family'
  },
  {
    key: 'flavor_organoleptic',
    label: 'Flavor'
  },
  {
    key: 'origin',
    label: 'Origin'
  },
  {
    key: 'origin-species',
    label: 'Origin Species'
  },
  {
    key: 'regional-availability',
    label: 'Regions available'
  },
  {
    key: 'countriesAvailable',
    label: 'Countries available'
  },
  {
    key: 'certifications',
    label: 'Certifications'
  },
  {
    key: 'rci',
    label: 'Renewable Carbon Index (RCI)'
  },
  {
    key: 'shelf-life',
    label: 'Shelf life'
  }
];

export enum SupplierFilterTarget {
  SUPPLIERS = 'SUPPLIERS',
  INGREDIENTS = 'INGREDIENTS'
}

const filterSuppliers: (FilterOption & {
  filterTarget: SupplierFilterTarget;
})[] = [
  {
    key: 'supplier_type',
    label: 'Company Type',
    type: CustomAttributesTypes.SINGLE_SELECT,
    filterTarget: SupplierFilterTarget.SUPPLIERS
  },
  {
    key: 'country_key',
    label: 'Headquarters country',
    type: CustomAttributesTypes.SINGLE_SELECT,
    filterTarget: SupplierFilterTarget.SUPPLIERS
  },
  {
    key: 'ingredient_applications',
    label: 'Ingredient Applications',
    type: CustomAttributesTypes.SINGLE_SELECT,
    filterTarget: SupplierFilterTarget.INGREDIENTS
  },
  {
    key: 'ingredient_functions',
    label: 'Ingredient Functions',
    type: CustomAttributesTypes.SINGLE_SELECT,
    filterTarget: SupplierFilterTarget.INGREDIENTS
  },
  {
    key: 'verified',
    label: 'Verified Companies',
    value: true,
    type: FilterFacetsTypes.BOOLEAN,
    filterTarget: SupplierFilterTarget.SUPPLIERS
  },
  {
    key: 'indie_brand_friendly',
    label: 'Indie Brand Friendly',
    value: true,
    type: FilterFacetsTypes.BOOLEAN,
    filterTarget: SupplierFilterTarget.SUPPLIERS
  }
];
export const compareSupplierListIngredients: CompareOption[] = [
  {
    key: 'name',
    label: 'Company name'
  },
  {
    key: 'headquarters',
    label: 'Headquarters'
  },
  {
    key: 'number_employees',
    label: 'Number of employees'
  }
];

export const getFilterOptions = (
  target: SearchTarget,
  segment?: SegmentTag
): FilterOption[] | (FilterOption & { target: SearchTarget })[] => {
  switch (target) {
    case ProductType.FORMULATIONS:
      return filterOptionsListFormulations;
    case ProductType.SERVICES:
      return filterOptionsListServices;
    case ProductType.INGREDIENTS:
      if (segment && segment === SegmentTag.FOOD) {
        return filterOptionsListFoodIngredients;
      } else {
        return filterOptionsListIngredients;
      }
    case SUPPLIER_SEARCH_TARGET:
      return filterSuppliers;
    default:
      return filterOptionsListIngredients;
  }
};

export const getFoodItemInfo = (searchDocument: SearchDocument) => {
  const itemInfo: string[] = [];

  for (const property of foodInfoDisplayPriorityList) {
    if (itemInfo.length === 4) break;
    switch (property) {
      case 'inci':
      case 'cas':
      case 'e':
      case 'ec':
      case 'fema':
      case 'flavis':
      case 'iupac': {
        const identifierProperty = property as keyof Identifiers;
        const identifiers: string[] =
          searchDocument?.identifiers?.[identifierProperty];
        if (identifiers && identifiers.length > 0) {
          itemInfo.push(...identifiers.filter(id => id !== ''));
        }
        break;
      }
      case 'claims':
      case 'food-categories':
      case 'organoleptic':
      case 'shelf-life':
      case 'functions':
      case 'applications':
      case 'certifications':
      case 'chemicalgroup':
      case 'properties': {
        const technicalProperty = property as keyof CategoryType;
        const categoryDetails: CategoryDetail[] | CategoryDetail =
          searchDocument?.categories?.technical?.[technicalProperty];
        if (categoryDetails instanceof Array && categoryDetails.length > 0) {
          itemInfo.push(...categoryDetails.map(detail => detail.value));
        } else if ((categoryDetails as CategoryDetail)?.value)
          itemInfo.push((categoryDetails as CategoryDetail).value);

        break;
      }
    }
  }
  return itemInfo.filter(i => i).join(', ');
};

export const getItemInfo = (
  searchDocument: SearchDocument,
  truncated = true
) => {
  let itemInfo = '';
  itemInfoDisplayPriorityList.every(property => {
    switch (property) {
      case 'inci':
      case 'cas':
      case 'iupac': {
        const identifierProperty = property as keyof Identifiers;
        const identifiers: string[] =
          searchDocument?.identifiers?.[identifierProperty];
        if (identifiers && identifiers.length > 0) {
          itemInfo = convertArrayToCommaString(identifiers);
        }
        break;
      }
      case 'claims':
      case 'functions':
      case 'applications':
      case 'certifications':
      case 'chemicalgroup':
      case 'organoleptic':
      case 'properties': {
        const technicalProperty = property as keyof CategoryType;
        const categoryDetails: CategoryDetail[] | CategoryDetail =
          searchDocument?.categories?.technical?.[technicalProperty];
        itemInfo = getCategoryValues(categoryDetails);
        break;
      }
    }

    if (itemInfo) {
      return false;
    }

    return true;
  });

  return itemInfo?.length > 60 && truncated
    ? itemInfo.substring(0, 60) + '...'
    : itemInfo;
};

export const getCompanyInfoByProperty = (
  key: string,
  searchDocument: SearchDocument
) => {
  const companyProperty = key as keyof SearchCompanyInfo;
  return searchDocument.company[companyProperty];
};

export const getIngredientInfoByProperty = (
  key: string,
  searchDocument: SearchDocument | IngredientsList //for admin space table
) => {
  switch (key) {
    case 'inci':
    case 'cas':
    case 'iupac':
    case 'ec':
    case 'fema':
    case 'flavis':
    case 'jecfa':
    case 'coe':
    case 'e':
    case 'general_name': {
      const identifierProperty = key as keyof Identifiers;
      const identifiers: string[] =
        searchDocument?.identifiers?.[identifierProperty];
      if (identifiers && identifiers.length > 0) {
        return convertArrayToCommaString(identifiers);
      }
      return;
    }
    case 'functions':
    case 'applications':
    case 'certifications':
    case 'chemicalgroup':
    case 'rci':
    case 'usagelevel':
    case 'origin-species':
    case 'appearance_organoleptic':
    case 'colors_organoleptic':
    case 'flavor_organoleptic':
    case 'flavor_family_organoleptic':
    case 'performance_claims':
    case 'sustainability_claims':
    case 'free_from_claims':
    case 'physical_properties':
    case 'chemical_properties':
    case 'food-categories':
    case 'active-components':
    case 'shelf-life':
    case 'origin': {
      const hasUnderscore = key.includes('_');
      if (hasUnderscore) {
        const keyFilterArray: string[] = key.split('_');
        const lastIndex = keyFilterArray.slice(0, -1).join('_').length;

        if (lastIndex !== -1) {
          const firstPart = key.substring(0, lastIndex);
          const secondPart = key.substring(lastIndex + 1);

          // Update keyFilterArray to contain the split parts
          keyFilterArray.length = 0;
          keyFilterArray.push(firstPart, secondPart);
        }

        // const keyFilterArray: string[] = key.split('_');
        const technicalProperty = keyFilterArray[1] as keyof CategoryType;
        const categoryDetails: CategoryDetail[] | CategoryDetail =
          searchDocument?.categories?.technical?.[technicalProperty];
        const categoryType =
          keyFilterArray[0].toUpperCase() as keyof typeof SearchCategoryType;
        const categoryTypeEnum: SearchCategoryType =
          SearchCategoryType[categoryType];
        return getCategoryValues(categoryDetails, categoryTypeEnum);
      } else {
        const technicalProperty = key as keyof CategoryType;
        const categoryDetails: CategoryDetail[] | CategoryDetail =
          searchDocument?.categories?.technical?.[technicalProperty];
        return getCategoryValues(categoryDetails);
      }
    }
    case 'segment': {
      const i18nLabel = 'reference.segments.' + searchDocument?.segment;
      return t(i18nLabel);
    }
    case 'regional-availability':
      return getCategoryValues(searchDocument?.categories?.[key]?.regions);
    case 'countriesAvailable':
      return getCategoryValues(
        searchDocument?.categories?.['regional-availability']?.countries
      );
    case 'hazardclassification':
    case 'hazardstatements':
      return getCategoryValues(searchDocument?.categories?.compliance?.[key]);
    default:
      return '';
  }
};

const getCategoryValues = (
  categoryDetails: CategoryDetail[] | CategoryDetail,
  type?: SearchCategoryType
) => {
  if (type && Array.isArray(categoryDetails)) {
    categoryDetails = filterCategoryDetailByType(type, categoryDetails);
  }

  let values = '';
  if (Array.isArray(categoryDetails) && categoryDetails.length > 0) {
    //if categoryDetails is an array join all values together after sorting them alphabetically
    values = categoryDetails
      .map(detail => detail?.value ?? detail)
      .sort((a, b) => a.localeCompare(b))
      .join(', ');
  } else if (!Array.isArray(categoryDetails)) {
    values = categoryDetails?.value;
  }

  return values;
};
export const filterCategoryDetailByType = (
  type: SearchCategoryType,
  categoryDetails: CategoryDetail[]
) => categoryDetails.filter(detail => detail?.properties?.type === type);

export const getFiltersWithDifferentType = (
  filters: SearchFilter[],
  type: string
) => {
  const newFilters: SearchFilter[] = [];
  filters.forEach((filter: SearchFilter) => {
    if (type !== filter.field) {
      newFilters.push(filter);
    }
  });
  return newFilters;
};

export const filterActiveFiltersByKey = (
  key: string,
  activeFilters: SearchFilter[]
) => {
  const newFilters: SearchFilter[] = activeFilters.filter(
    (activeFilter: SearchFilter) => {
      return activeFilter.field === key;
    }
  );

  return newFilters ? newFilters : [];
};

export const parseUrlFilterParams = (
  searchParams: URLSearchParams,
  target: SearchTarget,
  brandPortalAttributesDefinitions?: UnionCustomAttributeProps[]
) => {
  //including it in the filterOptions array directly would result in showing it
  //as a filter, we could also prevent that by filtering while rendering the filters,
  //but imo this is cleaner and less effort
  const filterOptions: FilterOption[] = !IS_UXBEAUTY
    ? getFilterOptions(target)
    : getUxbFilterOptions(target);
  const filterKeys = [
    ...(brandPortalAttributesDefinitions?.map(d => d.key) ?? []),
    ...filterOptions.map(filter => filter.key),
    'launch_year',
    'events',
    'award_category_key',
    'award_year',
    'trends'
  ];

  const searchFilters = filterKeys
    .filter(filterOptionKey => searchParams.has(filterOptionKey))
    .flatMap(filterOptionKey => {
      const filterValues = searchParams.get(filterOptionKey)?.split('|') || [];
      const isAwardFilter = ['award_category_key', 'award_year'].includes(
        filterOptionKey
      );
      const context = isAwardFilter
        ? searchParams.get('context') ?? undefined
        : undefined;

      return filterValues.map(filterValue => {
        return {
          field: filterOptionKey,
          value: filterValue,
          label: '',
          context
        };
      });
    });
  return searchFilters;
};

export const updateUrlFilterParams = (
  searchParams: URLSearchParams,
  filterList: SearchFilter[],
  property: string,
  clickedFilter: boolean,
  filters?: SearchFilter[]
) => {
  if (filterList.length > 0) {
    const filterValues = filterList.map(filter => filter.value).join('|');
    searchParams.set(property, filterValues);
  } else {
    searchParams.delete(property);
  }

  const allCurrentFilters = filters ? [...filters, ...filterList] : filterList;

  const filterWithContext = allCurrentFilters.find(filter => filter.context);

  if (filterWithContext?.context && clickedFilter) {
    searchParams.set('context', filterWithContext.context);
  } else {
    searchParams.delete('context');
  }
};
export const combineCustomAttributes = (
  productsAttributes: UnionCustomAttributeProps[] | undefined,
  companiesAttributes: UnionCustomAttributeProps[] | undefined
) => {
  return [...(productsAttributes ?? []), ...(companiesAttributes ?? [])];
};

export const clearAllUrlFilterParams = (
  searchParams: URLSearchParams,
  target: SearchTarget,
  attributesDefinitions?: UnionCustomAttributeProps[]
) => {
  const filterOptions: FilterOption[] = !IS_UXBEAUTY
    ? getFilterOptions(target)
    : getUxbFilterOptions(target);

  [
    ...filterOptions.map(filterOption => filterOption.key),
    ...(attributesDefinitions?.map(a => a.key) ?? []),
    'launch_year',
    'events',
    'award_category_key',
    'award_year',
    'trends',
    'context'
  ].forEach(filterOption => searchParams.delete(filterOption));
};

const translateFacetValue = (facetKey: string, facetValue: string) => {
  const i18nPath = `products.facetValues.${facetKey}.${facetValue}`;
  return t(i18nPath) !== i18nPath ? t(i18nPath) : facetValue;
};

export const transformFacetValue = (option: string, facetOption: Facet) => {
  let displayValue = '';
  switch (option) {
    case 'services':
      displayValue = translate(facetOption.value, ServiceTypes);
      break;
    case 'countries':
    case 'country_key':
      //TODO: we show some empty values for some companies
      displayValue =
        translate(facetOption.value, Countries) ?? facetOption.value ?? '';
      break;
    case 'regions':
      displayValue = translate(facetOption.value, Regions);
      break;
    case 'document_types':
      displayValue = translate(facetOption.value, AssetTypes);
      break;
    case 'supplier_type':
      displayValue = translate(facetOption.value, CompanyTypes);
      break;
    default:
      displayValue = translateFacetValue(option, facetOption.value);
  }
  return displayValue?.trim();
};

export const mapSearchStateToRequest = (state: SearchState): SearchRequest => {
  return {
    query: state.query,
    target: state.target,
    segment: state.segment,
    from: state.from,
    size: state.size,
    filters: state.filters || [],
    sort: state.sort,
    eventKey: state.eventKey
  };
};

export const getInitialSortState = (target: SearchTarget) =>
  IS_UXBEAUTY
    ? [
        {
          field: SearchSortField.EVENT_RANK,
          direction: SortDirection.ASCENDENT,
          nested_field: SearchSortNestedField.EVENT
        },
        target === ProductType.INGREDIENTS
          ? {
              field: SearchSortField.EVENT_DISPLAY_NAME,
              direction: SortDirection.ASCENDENT,
              nested_field: SearchSortNestedField.EVENT
            }
          : {
              field: SearchSortField.EVENT_CREATED_AT,
              direction: SortDirection.DESCENDENT,
              nested_field: SearchSortNestedField.EVENT
            }
      ]
    : [
        {
          field: generateRandomSortSeed() as SearchSortField.RANDOM,
          direction: SortDirection.RANDOM
        }
      ];
